import React, { useCallback } from 'react';
import addHours from 'date-fns/addHours';
import isBefore from 'date-fns/isBefore';
import _isEmpty from 'lodash/isEmpty';

import { useTranslation } from '@lyearn/i18n';
import { InfoOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { DatePickerDate, DateVariant, InfoPill, Stack, Typography } from '@lyearn/ui';
import DatePicker from '@/components/DatePicker';
import { DateRangePickerProps } from '@/components/DateRangePicker';
import TimePicker from '@/components/TimePicker';
import { setTimeToTargetFromDate } from '@/components/TimePicker/utils';

const RecurringDateRange: React.FC<
  DateRangePickerProps & { warnings?: string[]; direction?: 'row' | 'col' }
> = ({
  error,
  values: { startTime, endTime },
  onChange,
  disabled,
  direction = 'row',
  warnings,
}) => {
  const { t } = useTranslation();
  const isError = Boolean(error);

  const onChangeDate = useCallback(
    (value: DatePickerDate) => {
      if (!value) {
        return;
      }

      onChange(
        setTimeToTargetFromDate(value, startTime).toISOString(),
        setTimeToTargetFromDate(value, endTime).toISOString(),
      );
    },
    [endTime, onChange, startTime],
  );

  const onChangeStartTime = useCallback(
    (value: DatePickerDate) => {
      let updatedEndTime = endTime;

      if (!isBefore(value!, updatedEndTime)) {
        updatedEndTime = addHours(value!, 1);
      }

      value && onChange(value.toISOString(), updatedEndTime.toISOString());
    },
    [endTime, onChange],
  );

  const onChangeEndTime = useCallback(
    (value: DatePickerDate) => {
      value &&
        onChange(
          startTime.toISOString(),
          setTimeToTargetFromDate(startTime, value, { setSeconds: true }).toISOString(),
        );
    },
    [onChange, startTime],
  );

  return (
    <>
      <Stack alignItems="center" className="space-s-8" direction={direction} gapY={32}>
        <Stack
          className={cn('space-y-8', direction === 'row' ? 'w-2/5' : 'w-full')}
          direction="col">
          <Typography className="ms-8" color="text-text-primary" variant="body-short-02">
            {t('Common:Form.StartDate')}
          </Typography>
          <Stack className={cn({ 'pointer-events-none bg-bg-secondary opacity-50': disabled })}>
            <DatePicker
              disabled={disabled}
              pickerProps={{
                variant: DateVariant.Date,
                selectedTime: startTime,
                onChange: onChangeDate,
              }}
              showActionButtons={false}
            />
          </Stack>
        </Stack>
        <Stack className={direction === 'row' ? 'w-3/5' : 'w-full'} direction="row" gapX={8}>
          <Stack className="w-1/2" direction="col" gapY={8}>
            <Typography className="ms-8" color="text-text-primary" variant="body-short-02">
              {t('Common:Form.StartTime')}
            </Typography>
            <Stack
              className={cn('w-full', {
                'pointer-events-none bg-bg-secondary opacity-50': disabled,
              })}>
              <TimePicker
                disableZero
                className="w-full"
                value={startTime}
                onChange={onChangeStartTime}
              />
            </Stack>
          </Stack>
          <Stack className="w-1/2" direction="col" gapY={8}>
            <Typography className="ms-8" color="text-text-primary" variant="body-short-02">
              {t('Common:Form.EndTime')}
            </Typography>
            <Stack className={cn({ 'pointer-events-none bg-bg-secondary opacity-50': disabled })}>
              <TimePicker
                disableZero
                className="w-full"
                value={endTime}
                onChange={onChangeEndTime}
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      {isError ? (
        <Typography className="mt-8 ps-8" color="text-text-error" variant="body-short-03">
          {error}
        </Typography>
      ) : null}
      {_isEmpty(warnings) ? null : (
        <Stack className="mt-16" direction="col" gapY={8}>
          {warnings?.map((warning) => (
            <InfoPill
              key={warning}
              Icon={InfoOutline}
              appearance="secondary"
              label={warning}
              shape="square"
              type="icon"
              variant="filled"
            />
          ))}
        </Stack>
      )}
    </>
  );
};

export default RecurringDateRange;
